import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useSelector } from "react-redux";
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  FileSpreadsheet,
  Pencil,
  Trash2,
  Users,
  Wallet,
} from "lucide-react";
import { cleanGhanaPhone } from "../../utils/ghanaPhone";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import StatusBadge from "../../components/dashboard/StatusBadge";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { PORTFOLIO_RECORDS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { formatCurrency, formatDate } from "../../data/mock/dashboardData";
import { usePortfolios } from "../../context/PortfolioContext";
import { fetchPortfolioUploadRecords } from "../../services/portfolioUploadsApi";
import { showError, showSuccess } from "../../utils/toast";
import { personNameColumns, splitPersonName } from "../../utils/personName";

function ConfirmDeleteModal({ customer, onClose, onConfirm }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <h2 className="text-lg font-bold text-primary">Delete customer</h2>
          <p className="mt-1 text-sm text-primary/55">
            Remove <span className="font-semibold text-primary">{customer.customer_name}</span> (
            {customer.account_number}) from this upload? This cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-600/20"
            onClick={onConfirm}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditCustomerModal({ customer, onClose, onSave }) {
  const names = splitPersonName(customer);
  const [form, setForm] = useState({
    customer_number: customer.customer_number ?? "",
    first_name: names.first === "—" ? "" : names.first,
    last_name: names.last === "—" ? "" : names.last,
    account_number: customer.account_number ?? "",
    phone: customer.phone ?? "",
    amount_owed: customer.amount_owed ?? customer.outstanding_amount ?? "",
    due_date: customer.due_date ?? "",
  });

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const canSave =
    String(form.customer_number).trim() &&
    String(form.first_name).trim() &&
    String(form.last_name).trim() &&
    String(form.account_number).trim() &&
    String(form.phone).trim() &&
    String(form.due_date).trim() &&
    Number(form.amount_owed) > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;

    const customerNumber = cleanGhanaPhone(form.customer_number);
    const phone = cleanGhanaPhone(form.phone);
    if (!customerNumber) {
      showError("Customer number must be a valid Ghana mobile number (e.g. 024XXXXXXX).");
      return;
    }
    if (!phone) {
      showError("Mobile number must be a valid Ghana mobile number (e.g. 024XXXXXXX).");
      return;
    }

    onSave({
      customer_number: customerNumber,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      customer_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
      account_number: form.account_number.trim(),
      phone,
      amount_owed: Number(form.amount_owed),
      outstanding_amount: Number(form.amount_owed),
      due_date: form.due_date,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <form
        role="dialog"
        aria-modal="true"
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="border-b border-primary/[0.06] px-5 py-4">
          <h2 className="text-lg font-bold text-primary">Edit customer</h2>
          <p className="mt-1 text-sm text-primary/55">Update account details for this uploaded row.</p>
        </div>

        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Customer number</label>
            <input
              value={form.customer_number}
              onChange={(e) => setField("customer_number", e.target.value)}
              placeholder="024XXXXXXX"
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Account number</label>
            <input
              value={form.account_number}
              onChange={(e) => setField("account_number", e.target.value)}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">First name</label>
            <input
              value={form.first_name}
              onChange={(e) => setField("first_name", e.target.value)}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Last name</label>
            <input
              value={form.last_name}
              onChange={(e) => setField("last_name", e.target.value)}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Mobile number</label>
            <input
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="024XXXXXXX"
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Due date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setField("due_date", e.target.value)}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-primary/50">Current balance / arrears</label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={form.amount_owed}
              onChange={(e) => setField("amount_owed", e.target.value)}
              className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-primary/[0.06] px-5 py-4">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={!canSave}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function PortfolioUploadRecordsPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { groupId, uploadId } = useParams();
  const {
    getUpload,
    ensureGroup,
    ensureUploadRecords,
    updateUploadRecord,
    deleteUploadRecord,
  } = usePortfolios();
  const { group, upload } = getUpload(groupId, uploadId);

  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [recordsError, setRecordsError] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteCustomer, setDeleteCustomer] = useState(null);

  useEffect(() => {
    if (groupId && !group) {
      ensureGroup(groupId).catch(() => {});
    }
  }, [groupId, group, ensureGroup]);

  useEffect(() => {
    if (useLiveApi || !groupId || !uploadId) return;
    ensureUploadRecords(groupId, uploadId);
  }, [useLiveApi, groupId, uploadId, ensureUploadRecords]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setRecordsError(null);

      if (!groupId || !uploadId) {
        if (!cancelled) {
          setRecords([]);
          setLoadingRecords(false);
        }
        return;
      }

      if (!useLiveApi) {
        if (!cancelled) {
          setRecords(upload?.records ?? []);
          setLoadingRecords(false);
        }
        return;
      }

      setLoadingRecords(true);
      const slug = group?.group_slug || groupId;
      try {
        const rows = await fetchPortfolioUploadRecords(token, slug, uploadId);
        if (!cancelled) setRecords(rows || []);
      } catch (err) {
        if (!cancelled) {
          setRecordsError(err.message || "Could not load upload cases.");
          showError(err.message || "Could not load upload cases.");
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoadingRecords(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    useLiveApi,
    groupId,
    uploadId,
    group?.group_slug,
    upload?.records,
  ]);

  // Live API can still return records even if context has not hydrated the upload metadata yet.
  const resolvedUpload =
    upload ||
    (useLiveApi && uploadId
      ? {
          id: String(uploadId),
          fileName: `Upload ${uploadId}`,
          accounts: records.length,
          totalOwed: records.reduce((sum, row) => sum + (Number(row.amount_owed) || 0), 0),
        }
      : null);

  if (!group && !loadingRecords && !useLiveApi) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">Upload not found</p>
        <Link to="/dashboard/portfolios" className="mt-4 inline-block text-sm font-bold text-primary underline">
          Back to portfolios
        </Link>
      </div>
    );
  }

  if (!resolvedUpload && loadingRecords) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">Loading upload…</p>
      </div>
    );
  }

  if (!resolvedUpload && !loadingRecords && records.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <p className="font-semibold text-primary">
          {recordsError || "Upload not found"}
        </p>
        <Link to="/dashboard/portfolios" className="mt-4 inline-block text-sm font-bold text-primary underline">
          Back to portfolios
        </Link>
      </div>
    );
  }

  const displayUpload = resolvedUpload || {
    id: String(uploadId),
    fileName: `Upload ${uploadId}`,
    accounts: records.length,
    totalOwed: 0,
  };

  const totalOwed =
    records.reduce((sum, row) => sum + (Number(row.amount_owed) || 0), 0) ||
    displayUpload.totalOwed ||
    0;
  const avgOutstanding =
    records.length > 0
      ? Math.round(totalOwed / records.length)
      : displayUpload.accounts > 0
        ? Math.round((displayUpload.totalOwed || 0) / displayUpload.accounts)
        : 0;

  const columns = [
    { key: "numericId", header: "ID", type: "id" },
    { key: "case_id", header: "Case ID", type: "id" },
    { key: "customer_number", header: "Customer no.", type: "id" },
    ...personNameColumns(),
    { key: "account_number", header: "Account", type: "id" },
    { key: "outstanding", header: "Outstanding", type: "currency" },
    { key: "phone", header: "Mobile", type: "phone" },
    { key: "email", header: "Email" },
    { key: "ghana_card_number", header: "Ghana card", type: "id" },
    { key: "gender", header: "Gender" },
    { key: "date_of_birth", header: "Date of birth", type: "date" },
    { key: "country", header: "Country" },
    { key: "region", header: "Region" },
    { key: "exact_area", header: "Exact area" },
    { key: "gps_address", header: "GPS address" },
    { key: "loan_amount", header: "Loan amount", type: "currency" },
    { key: "amount_owed", header: "Amount owed", type: "currency" },
    { key: "amount_paid", header: "Amount paid", type: "currency" },
    { key: "due_date", header: "Due date", type: "date" },
    { key: "last_payment_date", header: "Last payment", type: "date" },
    { key: "daysLate", header: "Days late", type: "number" },
    ...(!useLiveApi
      ? [
          {
            key: "actions",
            header: "Actions",
            actions: (row) => [
              {
                key: "edit",
                label: "Edit customer",
                description: "Update name, phone, amounts, or product",
                icon: Pencil,
                tone: "primary",
                onClick: () => setEditCustomer(row),
              },
              {
                key: "delete",
                label: "Delete customer",
                description: "Remove this account from the upload",
                icon: Trash2,
                tone: "amber",
                onClick: () => setDeleteCustomer(row),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-full bg-surface">
      <div className="border-b border-primary/[0.08] bg-white">
        <div className="mx-auto max-w-8xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <Link
                to="/dashboard/portfolios"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/10 text-primary/60 transition-colors hover:bg-primary/[0.04] hover:text-primary"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/35">
                  {group?.name || "Portfolio group"}
                </p>
                <h1 className="truncate text-xl font-bold text-primary">{displayUpload.fileName}</h1>
                <p className="mt-1 text-sm text-primary/45">
                  {displayUpload.uploadedAt
                    ? `Uploaded ${formatDate(displayUpload.uploadedAt)} · `
                    : ""}
                  {(records.length || displayUpload.accounts || 0).toLocaleString()} accounts
                </p>
              </div>
            </div>
            <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
              <StatusBadge status={displayUpload.status || "completed"} />
              {(displayUpload.stage === "cases_created" || records.length > 0) && (
                <span className="shrink-0 whitespace-nowrap rounded-lg bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-600/10">
                  Cases created
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-8xl px-4 py-6 sm:px-6 lg:px-8">
        <StatCardGrid cols={3}>
          <StatCard
            label="Accounts imported"
            value={(records.length || displayUpload.accounts || 0).toLocaleString()}
            sub={`${records.length.toLocaleString()} cases in this file`}
            icon={Users}
            tone="primary"
          />
          <StatCard
            label="Total outstanding"
            value={formatCurrency(totalOwed)}
            sub={`Avg ${formatCurrency(avgOutstanding)} per account`}
            icon={Wallet}
            tone="accent"
          />
          <StatCard
            label="Recovery cases"
            value={(records.length || displayUpload.casesCreated || 0).toLocaleString()}
            sub={
              displayUpload.stage === "cases_created" || records.length > 0
                ? "Cases created from this file"
                : "Processing — cases pending"
            }
            icon={displayUpload.invalidRows ? AlertTriangle : Briefcase}
            tone={displayUpload.stage === "cases_created" || records.length > 0 ? "primary" : "amber"}
          />
        </StatCardGrid>

        <div className="rounded-2xl border border-primary/[0.07] bg-white shadow-sm">
          <div className="border-b border-primary/[0.06] px-5 py-4 sm:px-6">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary/40" />
              <h2 className="text-sm font-bold text-primary">Cases in this upload</h2>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {recordsError ? <p className="mb-3 text-sm text-rose-600">{recordsError}</p> : null}
            {loadingRecords ? (
              <p className="text-sm text-primary/45">Loading cases…</p>
            ) : (
              <FilteredDataTable
                columns={columns}
                data={records}
                config={{ ...PORTFOLIO_RECORDS_FILTER_CONFIG, title: "Case filters" }}
                pageSize={12}
                maxVisibleColumns={6}
                emptyMessage="No cases found in this upload."
              />
            )}
          </div>
        </div>
      </div>

      {editCustomer && (
        <EditCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSave={(patch) => {
            updateUploadRecord(groupId, uploadId, editCustomer.id, patch);
            setRecords((prev) =>
              prev.map((row) => (row.id === editCustomer.id ? { ...row, ...patch } : row))
            );
            setEditCustomer(null);
            showSuccess(`Updated ${patch.customer_name}.`);
          }}
        />
      )}

      {deleteCustomer && (
        <ConfirmDeleteModal
          customer={deleteCustomer}
          onClose={() => setDeleteCustomer(null)}
          onConfirm={() => {
            deleteUploadRecord(groupId, uploadId, deleteCustomer.id);
            setRecords((prev) => prev.filter((row) => row.id !== deleteCustomer.id));
            setDeleteCustomer(null);
            showSuccess(`Deleted ${deleteCustomer.customer_name}.`);
          }}
        />
      )}
    </div>
  );
}
